import DocumentShell, { DocSection } from './DocumentShell';
import PaymentSummary from './PaymentSummary';
import { fmtDay } from '../data/common';

const name = (t) => [t.title, t.firstName, t.lastName].filter(Boolean).join(' ');

/** Age, date of birth or ID shown next to each traveller, whichever the booking collected. */
const travellerInfo = (t) => [
  t.gender,
  t.age != null && t.age !== '' && `${t.age} yrs`,
  t.dob && `DOB ${fmtDay(t.dob)}`,
  t.passport && `Passport ${t.passport}`,
].filter(Boolean).join(' · ') || '—';

/**
 * Printable voucher for modules without a dedicated document (activities, insurance, packages, cabs):
 * the module's own booking summary, travellers, payment and policy, in the shared A4 letterhead.
 */
export default function BookingVoucher({ booking, title, refLabel, Summary, policy = [], info = [] }) {
  const notice = booking.status === 'cancelled'
    ? `This booking has been cancelled. This ${title.toLowerCase()} is no longer valid.`
    : booking.status === 'pending'
      ? 'This booking is still being confirmed. Carry the updated document once it is confirmed.'
      : null;

  return (
    <DocumentShell
      title={title}
      subtitle={[booking.title, booking.travelDate && fmtDay(booking.travelDate)].filter(Boolean).join(' · ')}
      booking={booking}
      backTo={`/${booking.module}/bookings/${booking.id}`}
      references={[
        [refLabel, booking.ref, true],
        ['Booking ID', booking.id],
        ...(booking.supplierBookingId && booking.supplierBookingId !== booking.ref
          ? [[`${booking.supplierCode || 'Supplier'} reference`, booking.supplierBookingId]] : []),
      ]}
      barcode={booking.supplierBookingId || booking.ref || booking.id}
      notice={notice}
    >
      <DocSection title="Booking" className="doc-summary">
        {booking.subtitle && <p className="doc-small">{booking.subtitle}</p>}
        {booking.summary && <Summary summary={booking.summary} />}
      </DocSection>

      <DocSection title="Travellers">
        <table className="doc-table">
          <thead><tr><th>#</th><th>Name</th><th>Type</th><th>Details</th></tr></thead>
          <tbody>
            {booking.travellers.map((t, i) => (
              <tr key={i}><td>{i + 1}</td><td><b>{name(t) || '—'}</b></td><td>{t.type || '—'}</td><td>{travellerInfo(t)}</td></tr>
            ))}
          </tbody>
        </table>
        <p className="doc-small">Contact: {booking.contact.phone} · {booking.contact.email}{booking.contact.gstin ? ` · GSTIN ${booking.contact.gstin} (${booking.contact.company})` : ''}</p>
      </DocSection>

      <div className="doc-columns">
        {policy.length > 0 && (
          <DocSection title="Cancellation policy">
            <ul className="doc-list">{policy.map((p) => <li key={p}>{p}</li>)}</ul>
          </DocSection>
        )}
        <PaymentSummary booking={booking} />
      </div>

      {info.length > 0 && (
        <DocSection title="Important information" className="doc-info">
          <ul>{info.map((line) => <li key={line}>{line}</li>)}</ul>
        </DocSection>
      )}
    </DocumentShell>
  );
}
